import {
  AbcHttpInterceptorModule,
  ENVIRONMENTS,
} from '@absg/angular-common';
import { IconsComponent, SpinnerComponent, StatusPillsComponent } from '@absg/angular-components';
import { AuthenticationModule, AuthorizationModule, PartyModule } from '@absg/angular-platform';
import { EmrIntegrationApiModule } from '@absg/api-emr-integration';
import { EntitlementsApiModule } from '@absg/api-entitlements';
import { FormularyApiModule } from '@absg/api-formulary';
import { IdentityApiModule } from '@absg/api-identity';
import { InventoryApiModule } from '@absg/api-inventory';
import { LookupApiModule } from '@absg/api-lookup';
import { PartyApiModule } from '@absg/api-party';
import { PatientApiModule } from '@absg/api-patient';
import { PricingApiModule } from '@absg/api-pricing';
import { PrintingApiModule } from '@absg/api-printing';
import { PurchaseOrderApiModule } from '@absg/api-purchase-order';
import { StationManagementApiModule } from '@absg/api-station-management';
import { E2EApiApiModule } from '@absg/e2e-api';
import { EnvironmentService, NucleusHardwareModule, PrinterService, ScannerDetectorComponent } from '@absg/nim-client-shared';
import { BarcodeSimulatorComponent } from '@absg/nim-client-shared/components/dev-tools';
import { provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { inject, Injector, NgModule, provideAppInitializer } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { BrowserModule } from '@angular/platform-browser';
import { provideAnimations } from '@angular/platform-browser/animations';
import { CalendarModule, DateAdapter } from 'angular-calendar';
import { adapterFactory } from 'angular-calendar/date-adapters/date-fns';
import { HotkeyModule } from 'angular2-hotkeys';
import { EnvironmentService as StationEnvironmentService } from 'core.services/environment.service';
import { IPCRenderPrinterService } from 'core.services/ipc-render-printer.service';
import { DoorAccessModalComponent } from 'shared.root/components/door-access-modal/door-access.component';
import { ResolveFailedDoorComponent } from 'shared.root/components/resolve-failed-door/resolve-failed-door.component';
import { StaticInjector } from 'shared.root/helpers/static-injector';

import { environment as dev } from '../../environments/environment.dev';
import { environment as local } from '../../environments/environment.local';
import { environment as prod } from '../../environments/environment.prod';
import { environment as stage } from '../../environments/environment.stage';
import { environment as stageweb } from '../../environments/environment.stageweb';
import { environment as test } from '../../environments/environment.test';
import { CoreModule } from '../core/core.module';
import { SharedModule } from '../shared/shared.module';
import { AuthStartComponent } from './auth-start/auth-start.component';
import { WebviewDirective } from './cms/web-view.directive';
import { DeviceRegistrationComponent } from './device-registration/device-registration.component';
import { E2ePrintInfoComponent } from './e2e-print-info/e2e-print-info.component';
import { FailedWindowComponent } from './failed-window/failed-window.component';
import { HelpCenterComponent } from './help-center/help-center.component';
import { LegalDisclaimerComponent } from './legal-disclaimer/legal-disclaimer.component';
import { MainComponent } from './main/main.component';
import { MainRoutingModule, routeComponents } from './main-routing.module';
import { PasswordResetComponent } from './password-reset/password-reset.component';
import { PrintNotesDevComponent } from './print-notes/print-dev-notes.component';
import { SwitchStationComponent } from './switchStation/switch-station.component';
import { TermsAndConditionsComponent } from './terms-and-conditions/terms-and-conditions.component';

const environments = {
  default: window['environment'] || local,
  dev,
  test,
  stage,
  stageweb,
  prod,
  local,
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function loadExternalConfiguration(config: StationEnvironmentService): () => Promise<any> {
  return () => {
    const promise = config.loadExternalConfiguration();
    return promise;
  };
}

export function getGoogleTagManagerId(config: EnvironmentService): string {
  return config.getGoogleTagManagerId();
}

@NgModule({
  declarations: [
    DeviceRegistrationComponent,
    MainComponent,
    routeComponents,
    PasswordResetComponent,
    LegalDisclaimerComponent,
    SwitchStationComponent,
    PrintNotesDevComponent,
    WebviewDirective,
    FailedWindowComponent,
    ResolveFailedDoorComponent,
    DoorAccessModalComponent,
    E2ePrintInfoComponent,
    HelpCenterComponent,
    AuthStartComponent,
    TermsAndConditionsComponent,
  ],
  bootstrap: [MainComponent],
  imports: [
    SharedModule,
    PartyApiModule.forRoot('party'),
    AuthenticationModule.forRoot(),
    AuthorizationModule.forRoot({ unauthorizedRoute: '/unauthorized' }),
    FormularyApiModule.forRoot('formulary'),
    IdentityApiModule.forRoot('identity'),
    InventoryApiModule.forRoot('inventory'),
    EntitlementsApiModule.forRoot('entitlements'),
    E2EApiApiModule.forRoot('e2e'),
    StationManagementApiModule.forRoot('stationmanagement'),
    LookupApiModule.forRoot('lookup'),
    PatientApiModule.forRoot('patient'),
    PricingApiModule.forRoot('pricing'),
    PurchaseOrderApiModule.forRoot('purchaseorder'),
    PrintingApiModule.forRoot('printing'),
    CalendarModule.forRoot({
      provide: DateAdapter,
      useFactory: adapterFactory,
    }),
    EmrIntegrationApiModule.forRoot('emrintegration'),
    PartyModule,
    HotkeyModule.forRoot(),
    NucleusHardwareModule.forRoot({
      provide: PrinterService, useClass: IPCRenderPrinterService,
    }),
    CoreModule.forRoot(),
    AbcHttpInterceptorModule.forRoot(),
    FormsModule,
    ReactiveFormsModule,
    BrowserModule,
    SpinnerComponent,
    StatusPillsComponent,
    IconsComponent,
    BarcodeSimulatorComponent,
    ScannerDetectorComponent,
    MainRoutingModule, // needs to be last import to ensure routes are correct
  ],
  providers: [
    provideAnimations(),
    {
      provide: ENVIRONMENTS,
      useValue: environments,
    },
    {
      provide: 'googleTagManagerId',
      useFactory: getGoogleTagManagerId,
      deps: [EnvironmentService],
      multi: true,
    },
    provideAppInitializer(() => {
      const initializerFn = (loadExternalConfiguration)(inject(StationEnvironmentService));
      return initializerFn();
    }),
    provideHttpClient(withInterceptorsFromDi()),
  ],
})
export class MainModule {
  constructor(private injector: Injector) {
    StaticInjector.injector = this.injector;
  }
}
